import React, { useState, useEffect } from 'react';
import RegionSelection from './RegionSelection';
import { Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import MultiSelectFilters from '../PortfolioMasterData/MultiSelectFilters';
import {
  setShowSave,
  setVotingTableData
} from '../../redux/FeedbackCollation/feedbackCollationSlice';
import { setDecisionVotingTableData } from '../../redux/DecisionCentral/decisionCentralRulesSlice';
import {
  steeringReco,
  functionalFeedback,
  regionalDecisions,
  finalDecisions,
  decisionCentral,
  filterByLevel
} from './utils';

const VotingFilter = ({
  tableName,
  isTableLoading,
  checkedRows,
  currentVotingTeam,
  votingData,
  setSelectedFilters,
  regionOption,
  showApply,
  fetchRegionalVotingData,
  userType,
  stepLevelState,
  votingTable,
  setCallReset = false,
  setCallApply,
  filteredData,
  customerFilter,
  selectedCustomer = null
}) => {
  const dispatch = useDispatch();
  const decisionVotingTableData = useSelector(
    (state) => state.decisionMakingCentralRules.votingTableData
  );
  const votingTableData = useSelector((state) => state.feedbackCollation.votingTableData);

  const [originalVotingTableData, setOriginalVotingTableData] = useState([]);
  const [selectedFilterValues, setSelectedFilterValues] = useState([]);
  const [filtersConfig, setFilterConfigs] = useState([]);
  const [isApplyButtonVisible, setIsApplyButtonVisible] = useState(false);

  useEffect(() => {
    if (checkedRows?.length > 0) {
      if (showApply) {
        dispatch(setShowSave(false));
      }
    } else {
      dispatch(setShowSave(true));
    }
  }, [showApply]);

  useEffect(() => {
    if (tableName === 'voting table' || tableName === 'decision table') {
      setIsApplyButtonVisible(true);
    }
  }, [tableName]);

  const currentVotingTableData =
    votingTable === 'Decison Table' ? decisionVotingTableData : votingData;

  useEffect(() => {
    // Reinitialize originalVotingTableData when data source changes
    if (currentVotingTableData?.length > 0 && originalVotingTableData.length === 0) {
      setOriginalVotingTableData([...currentVotingTableData]);
    }

    // Determine filterConfigs based on votingTable and currentVotingTeam
    if (votingTable === 'Decison Table') {
      setFilterConfigs(decisionCentral);
    } else {
      switch (currentVotingTeam) {
        case 'Steering Reco':
          setFilterConfigs(steeringReco);
          break;
        case 'Functional Feedback':
          setFilterConfigs(functionalFeedback);
          break;
        case 'Regional Decisions':
          setFilterConfigs(regionalDecisions);
          break;
        default:
          setFilterConfigs(finalDecisions);
      }
    }
  }, [currentVotingTeam, votingTable, currentVotingTableData]);

  const handleApplyFilters = (selectedValues = []) => {
    setSelectedFilterValues(selectedValues);
    if (setSelectedFilters) {
      setSelectedFilters(selectedValues);
    }

    const levelToFilter = selectedValues?.level_attribute_name?.length > 0 ? 2 : 1;

    const filterByLevelData = filterByLevel(currentVotingTableData, levelToFilter);

    const filteredData = [...filterByLevelData].filter((item) => {
      return filtersConfig.every(({ key }) => {
        const selectedValuesForKey = selectedValues[key] || [];
        const normalizedItemValue =
          key === 'votes aligned'
            ? item[key] === true
              ? 'Yes'
              : item[key] === false
                ? 'No'
                : ''
            : item[key];

        return (
          selectedValuesForKey.length === 0 ||
          (Array.isArray(normalizedItemValue)
            ? selectedValuesForKey.some((val) => normalizedItemValue.includes(val)) // Check for intersection
            : selectedValuesForKey.includes(normalizedItemValue)) // Direct match for non-array fields
        );
      });
    });
    setCallApply(selectedValues);
    dispatch(setVotingTableData(filteredData));
    dispatch(setDecisionVotingTableData(filteredData));
  };

  const handleResetFilters = () => {
    // Restore data from originalVotingTableData
    dispatch(setVotingTableData(originalVotingTableData));
    dispatch(setDecisionVotingTableData(originalVotingTableData));
    const originalFilters = {
      segment: '',
      brand: '',
      subbrand: '',
      size: '',
      sku: '',
      algoreco: '',
      assignedTo: '',
      region: null,
      customer: null,
      level_attribute_name: null
    };
    setSelectedFilterValues(originalFilters);
    if (setSelectedFilters) {
      setSelectedFilters(originalFilters);
    }
    setCallReset(true);
  };
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start', // Align children (RegionSelection and filters) at the top
        justifyContent: 'space-between',
        gap: '20px',
        width: '100%'
      }}>
      {/* Render RegionSelection for Regional Decisions */}
      {currentVotingTeam === 'Regional Decisions' && (
        <RegionSelection
          regions={regionOption}
          handleRegionFilterChange={(region) => {
            fetchRegionalVotingData(currentVotingTeam, stepLevelState, userType, region);
          }}
          sx={{
            zIndex: 1000,
            alignSelf: 'flex-start' // Ensure it aligns with the filters
          }}
          setSelectedRegionFilter={(region) => setSelectedFilters(region)}
        />
      )}

      {/* Scrollable Filters Section */}
      <Box
        sx={{
          display: 'flex',
          gap: '20px',
          flexGrow: 1,
          overflowX: 'auto',
          '&::-webkit-scrollbar': {
            height: '4px',
            backgroundColor: 'transparent'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '3px'
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.3)'
          }
        }}>
        {filtersConfig?.length > 0 && (
          <MultiSelectFilters
            tableName={tableName}
            selectedCustomer={selectedCustomer}
            isTableLoading={isTableLoading}
            filtersConfig={filtersConfig}
            data={
              filteredData ||
              (votingTable === 'Decison Table' ? decisionVotingTableData : votingTableData)
            }
            onApplyFilters={handleApplyFilters}
            onResetFilters={handleResetFilters}
            isApplyButtonVisible={false}
            selectedFilters={selectedFilterValues} // Pass the updated selected filter values
            customerFilter={customerFilter}
            sx={{
              margin: '0',
              padding: '10px'
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default VotingFilter;
